/**
 * ToolConfigDialog — modal that captures per-agent config for an MCP tool.
 *
 * The dialog is the **only** place a Creator types secret material in the
 * Network Workbench. The shape is driven by ``inputSchema.properties``
 * from ``GET /tools``:
 *
 *   - ``x-ui-type: text``      → single-line input (default)
 *   - ``x-ui-type: textarea``  → multi-line input (e.g. API_SPECIFICATION)
 *   - ``x-ui-type: password``  → masked input + writes to SSM via /tools/secrets
 *   - ``x-ui-type: select``    → ``<select>`` with ``enum`` options
 *   - ``x-secret: true``       → masked + secret-stored regardless of x-ui-type
 *
 * Behaviour mandated by the spec ("if the user cancels the config… remove
 * the tool from the multi tag like selection so that there no agent without
 * config"):
 *
 *   - If a Creator opens the dialog for a *new* tool entry and **cancels**,
 *     the parent picker drops the chip — call site supplies ``onCancel``.
 *   - If a Creator opens it for an *existing* entry and cancels, no
 *     change is committed; the chip stays. The parent decides which mode
 *     this is via the ``isNewEntry`` prop.
 *   - Save is disabled until every required field is satisfied; on save
 *     we ``putToolSecret`` for each non-empty masked field, then return
 *     ``{ instanceLabel, config, secretRefs, instanceId }`` to the parent.
 *
 * The dialog never echoes a saved secret back. Editing a tool that
 * already has a secret stored shows "•••• (saved)" with a "Replace…"
 * button; clicking it opens an input so the new value can be PUT to
 * the same SSM path.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  buildToolSecretPath,
  newToolInstanceId,
  putToolSecret,
} from '../../services/toolsApi';
import ToolGenericRestConfigWizard from './ToolGenericRestConfigWizard.jsx';
import ToolCodeConnectorAgentConfigWizard from './ToolCodeConnectorAgentConfigWizard.jsx';
import ToolCredBrokerConfigWizard from './ToolCredBrokerConfigWizard.jsx';

const SECRET_PLACEHOLDER = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (saved)';

function getProperties(schema) {
  return schema && typeof schema.properties === 'object' && schema.properties
    ? schema.properties
    : {};
}

function getRequired(schema) {
  return Array.isArray(schema?.required) ? schema.required : [];
}

function isSecretField(spec) {
  return Boolean(spec?.['x-secret']);
}

function fieldUiType(spec, secretFieldsHint) {
  const ui = spec?.['x-ui-type'];
  if (typeof ui === 'string' && ui) return ui;
  if (Array.isArray(secretFieldsHint) && secretFieldsHint.includes('__INSPECT__')) {
    // unreachable — kept to make the parameter shape explicit
  }
  if (Array.isArray(spec?.enum) && spec.enum.length) return 'select';
  return 'text';
}

function defaultPersistedValue(spec) {
  if (spec?.default !== undefined) return spec.default;
  if (Array.isArray(spec?.enum) && spec.enum.length) return spec.enum[0];
  if (spec?.type === 'boolean') return false;
  if (spec?.type === 'number' || spec?.type === 'integer') return '';
  return '';
}

/**
 * @param {{
 *   tool: { name: string, description?: string, inputSchema?: object, secretFields?: string[] },
 *   open: boolean,
 *   isNewEntry: boolean,
 *   initialEntry?: { instanceId?: string, instanceLabel?: string, config?: Record<string, any>, secretRefs?: Record<string, string> },
 *   networkId: string,
 *   agentId: string,
 *   env: string,
 *   tenantId: string,
 *   onSave: (entry: { name: string, instanceId: string, instanceLabel: string, config: Record<string, any>, secretRefs: Record<string, string> }) => void,
 *   onCancel: () => void,
 * }} props
 */
export default function ToolConfigDialog({
  tool,
  open,
  isNewEntry,
  initialEntry,
  networkId,
  agentId,
  env,
  tenantId,
  onSave,
  onCancel,
}) {
  const schema = tool?.inputSchema || {};
  const properties = useMemo(() => getProperties(schema), [schema]);
  const required = useMemo(() => getRequired(schema), [schema]);
  const fieldOrder = useMemo(() => Object.keys(properties), [properties]);

  const [instanceId] = useState(
    () => initialEntry?.instanceId || newToolInstanceId(),
  );
  const [values, setValues] = useState(() => {
    const seed = {};
    for (const key of fieldOrder) {
      const spec = properties[key];
      if (isSecretField(spec)) {
        seed[key] = '';
      } else {
        const existing = initialEntry?.config?.[key];
        seed[key] = existing !== undefined ? existing : defaultPersistedValue(spec);
      }
    }
    return seed;
  });
  const [secretReplacing, setSecretReplacing] = useState(() => {
    const m = {};
    for (const key of fieldOrder) {
      if (!isSecretField(properties[key])) continue;
      m[key] = isNewEntry || !initialEntry?.secretRefs?.[key];
    }
    return m;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [instanceLabel, setInstanceLabel] = useState(() =>
    String(initialEntry?.instanceLabel || '').trim(),
  );

  useEffect(() => {
    if (!open) setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setInstanceLabel(String(initialEntry?.instanceLabel || '').trim());
  }, [open, initialEntry?.instanceLabel]);

  if (!open || !tool) return null;

  if (tool.name === 'generic-rest-api') {
    return (
      <ToolGenericRestConfigWizard
        tool={tool}
        open={open}
        isNewEntry={isNewEntry}
        initialEntry={initialEntry}
        networkId={networkId}
        agentId={agentId}
        env={env}
        tenantId={tenantId}
        allowProbe={Boolean(networkId && String(networkId).trim() && networkId !== 'new')}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  if (tool.name === 'code-connector') {
    return (
      <ToolCodeConnectorAgentConfigWizard
        tool={tool}
        open={open}
        isNewEntry={isNewEntry}
        initialEntry={initialEntry}
        networkId={networkId}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  if (tool.name === 'cred-broker') {
    return (
      <ToolCredBrokerConfigWizard
        tool={tool}
        open={open}
        isNewEntry={isNewEntry}
        initialEntry={initialEntry}
        networkId={networkId}
        agentId={agentId}
        env={env}
        tenantId={tenantId}
        persistTarget="agent"
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  const update = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const startReplaceSecret = (key) => {
    setSecretReplacing((prev) => ({ ...prev, [key]: true }));
    setValues((prev) => ({ ...prev, [key]: '' }));
  };

  const cancelReplaceSecret = (key) => {
    setSecretReplacing((prev) => ({ ...prev, [key]: false }));
    setValues((prev) => ({ ...prev, [key]: '' }));
  };

  const missingRequired = required.filter((key) => {
    const spec = properties[key];
    const replacing = secretReplacing[key];
    if (isSecretField(spec)) {
      if (replacing) return !values[key] || String(values[key]).trim() === '';
      return !initialEntry?.secretRefs?.[key];
    }
    const v = values[key];
    return v === undefined || v === null || String(v).trim() === '';
  });

  // SSM Standard tier caps a SecureString value at 4 KB. A field marked ``x-max-bytes`` (or any
  // ``x-secret`` textarea, e.g. cred-broker's BOOTSTRAP_SCRIPT) is enforced here — block save +
  // show a live byte counter — so an over-limit script fails in the UI, not with an opaque
  // ``ValidationException`` from put_parameter at save time.
  const SSM_MAX_BYTES = 4096;
  const _byteLen = (s) => {
    try { return new TextEncoder().encode(String(s ?? '')).length; }
    catch { return String(s ?? '').length; }
  };
  const oversizeFields = fieldOrder.filter((key) => {
    const spec = properties[key];
    const capped = (fieldUiType(spec, tool.secretFields) === 'textarea') || isSecretField(spec);
    return capped && _byteLen(values[key]) > SSM_MAX_BYTES;
  });

  const canSave =
    missingRequired.length === 0 &&
    oversizeFields.length === 0 &&
    Boolean(String(instanceLabel).trim()) &&
    !submitting;

  const handleSave = async () => {
    setSubmitting(true);
    setError('');
    try {
      const config = {};
      const secretRefs = { ...(initialEntry?.secretRefs || {}) };
      for (const key of fieldOrder) {
        const spec = properties[key];
        const value = values[key];
        if (isSecretField(spec)) {
          if (!secretReplacing[key]) {
            // Untouched — keep the existing ref (already in secretRefs).
            continue;
          }
          if (!value || String(value).trim() === '') {
            // Optional secret left blank: drop the ref so the catalog row
            // doesn't claim it has one.
            delete secretRefs[key];
            continue;
          }
          const parameterName = buildToolSecretPath({
            env,
            tenantId,
            networkId,
            agentId,
            instanceId,
            configKey: key,
          });
          // eslint-disable-next-line no-await-in-loop
          await putToolSecret({ parameterName, value: String(value) });
          secretRefs[key] = parameterName;
        } else if (value !== undefined && value !== '') {
          config[key] = value;
        }
      }
      onSave({
        name: tool.name,
        instanceId,
        instanceLabel: String(instanceLabel).trim(),
        config,
        secretRefs,
      });
    } catch (exc) {
      setError(exc?.message || 'Failed to save tool config');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tool-config-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 id="tool-config-title" className="text-base font-semibold text-gray-900">
              Configure {tool.name}
            </h2>
            {tool.description && (
              <p className="mt-1 text-xs text-gray-600">{tool.description}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="px-2.5 py-1 text-xs font-medium text-gray-500 rounded-md hover:bg-gray-100"
          >
            ×
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label htmlFor="tool-cfg-instance-label" className="block text-sm font-medium text-gray-700 mb-1">
              Connection display name<span className="text-red-500"> *</span>
            </label>
            <input
              id="tool-cfg-instance-label"
              type="text"
              data-testid="tool-config-instance-label"
              autoComplete="off"
              value={instanceLabel}
              onChange={(e) => setInstanceLabel(e.target.value)}
              placeholder="e.g. Search — customer support workspace"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Distinct title for this tool on the agent (&quot;{tool.name}&quot;). Required so two mounts of the same tool stay identifiable.
            </p>
          </div>
          {fieldOrder.map((key) => {
            const spec = properties[key];
            const ui = fieldUiType(spec, tool.secretFields);
            const secret = isSecretField(spec);
            const replacing = secretReplacing[key];
            const value = values[key];
            const isReq = required.includes(key);
            const placeholder = spec?.description || '';
            const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
            const inputClass =
              'block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

            return (
              <div key={key}>
                <label htmlFor={`tool-cfg-${key}`} className={labelClass}>
                  {key}
                  {isReq && <span className="text-red-500"> *</span>}
                </label>
                {secret && !replacing ? (
                  <div className="flex items-center gap-2">
                    <input
                      id={`tool-cfg-${key}`}
                      type="text"
                      value={SECRET_PLACEHOLDER}
                      readOnly
                      className={`${inputClass} bg-gray-100 text-gray-500`}
                    />
                    <button
                      type="button"
                      onClick={() => startReplaceSecret(key)}
                      className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                    >
                      Replace
                    </button>
                  </div>
                ) : ui === 'textarea' ? (
                  <>
                    <textarea
                      id={`tool-cfg-${key}`}
                      value={value ?? ''}
                      onChange={(e) => update(key, e.target.value)}
                      placeholder={placeholder}
                      rows={secret ? 10 : 5}
                      className={inputClass}
                    />
                    {/* Byte counter for a size-capped field (SSM 4 KB). Turns red + blocks save
                        when over — so an oversized cred-broker script fails here, not at put_parameter. */}
                    {(secret || fieldUiType(spec, tool.secretFields) === 'textarea') && (() => {
                      const _n = _byteLen(value);
                      const _over = _n > SSM_MAX_BYTES;
                      return (
                        <p className={`mt-1 text-xs tabular-nums ${_over ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {_n.toLocaleString()} / {SSM_MAX_BYTES.toLocaleString()} bytes
                          {_over ? ' — too large; SSM caps a secret at 4 KB. Trim the script (mint creds at runtime instead of pasting large blobs).' : ''}
                        </p>
                      );
                    })()}
                  </>
                ) : ui === 'select' ? (
                  <select
                    id={`tool-cfg-${key}`}
                    value={value ?? ''}
                    onChange={(e) => update(key, e.target.value)}
                    className={inputClass}
                  >
                    {(spec?.enum || []).map((opt) => (
                      <option key={String(opt)} value={opt}>
                        {String(opt)}
                      </option>
                    ))}
                  </select>
                ) : ui === 'password' || secret ? (
                  <div className="flex items-center gap-2">
                    <input
                      id={`tool-cfg-${key}`}
                      type="password"
                      value={value ?? ''}
                      onChange={(e) => update(key, e.target.value)}
                      placeholder={placeholder}
                      autoComplete="new-password"
                      className={inputClass}
                    />
                    {secret && initialEntry?.secretRefs?.[key] && (
                      <button
                        type="button"
                        onClick={() => cancelReplaceSecret(key)}
                        className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                      >
                        Keep
                      </button>
                    )}
                  </div>
                ) : (
                  <input
                    id={`tool-cfg-${key}`}
                    type="text"
                    value={value ?? ''}
                    onChange={(e) => update(key, e.target.value)}
                    placeholder={placeholder}
                    className={inputClass}
                  />
                )}
                {placeholder && ui !== 'textarea' && (
                  <p className="mt-1 text-xs text-gray-500">{placeholder}</p>
                )}
              </div>
            );
          })}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-2.5 py-1 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
